/**
 * Pure helpers for Lab Data spend and budget views.
 */

/**
 * @returns {{ from: string, to: string }}
 */
export function getDefaultDateRange(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

/**
 * @param {*} value
 * @returns {string}
 */
export function formatNisAmount(value) {
  if (value == null || value === '') {
    return 'NIS 0.00';
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return `NIS ${String(value)}`;
  }
  return `NIS ${num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * @param {*} value
 * @returns {string}
 */
export function formatPercentUsed(value) {
  if (value == null || value === '') {
    return '0%';
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return String(value);
  }
  return `${num.toFixed(1)}%`;
}

/**
 * Bootstrap progress color for budget burn percent used.
 * 90% and above is danger, 70% and above is warning, otherwise success
 * (the neutral glass primary green).
 *
 * @param {*} percentUsed
 * @returns {'danger'|'warning'|'success'}
 */
export function getBurnProgressVariant(percentUsed) {
  const pct = Number(percentUsed);
  if (!Number.isFinite(pct)) return 'success';
  if (pct >= 90) return 'danger';
  if (pct >= 70) return 'warning';
  return 'success';
}

/**
 * @param {Object} params
 * @param {string} [params.from]
 * @param {string} [params.to]
 * @param {string|number|null} [params.budgetLineId] - maps to preferred `budget_id`
 * @param {string|number|null} [params.budgetId] - preferred filter key
 * @param {string|number|null} [params.projectId]
 * @param {string|number|null} [params.supplierId]
 * @param {string} [params.granularity]
 * @returns {URLSearchParams}
 */
export function buildLabDataQueryParams({
  from,
  to,
  budgetLineId,
  budgetId,
  projectId,
  supplierId,
  granularity,
} = {}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const resolvedBudgetId =
    budgetId != null && String(budgetId).trim() !== ''
      ? budgetId
      : budgetLineId != null && String(budgetLineId).trim() !== ''
        ? budgetLineId
        : null;
  if (resolvedBudgetId != null) {
    params.set('budget_id', String(resolvedBudgetId));
  }
  if (projectId != null && String(projectId).trim() !== '') {
    params.set('project_id', String(projectId));
  }
  if (supplierId != null && String(supplierId).trim() !== '') {
    params.set('supplier_id', String(supplierId));
  }
  if (granularity) params.set('granularity', granularity);
  return params;
}

/**
 * Format a spend by-period `period_start` (ISO date) for chart axis labels.
 *
 * @param {string} [periodStart] - e.g. `2026-01-01`
 * @param {'month'|'quarter'|'year'} [granularity='month']
 * @returns {string}
 */
export function formatSpendPeriodLabel(periodStart, granularity = 'month') {
  if (!periodStart) return '';
  const date = new Date(`${String(periodStart).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return String(periodStart);
  }
  const year = date.getFullYear();
  if (granularity === 'year') {
    return String(year);
  }
  if (granularity === 'quarter') {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `Q${quarter} ${year}`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Map API period rows to chart-ready points with visible month labels.
 *
 * @param {Array<{ period_start?: string, period?: string, label?: string, month?: string, total_nis?: string|number, amount?: string|number }>} periods
 * @param {'month'|'quarter'|'year'} [granularity='month']
 * @returns {Array<{ label: string, amount: number, periodStart: string }>}
 */
export function mapSpendPeriodsToChartData(periods = [], granularity = 'month') {
  return (periods || []).map(row => {
    const periodStart =
      row.period_start ?? row.period ?? row.label ?? row.month ?? '';
    return {
      label: formatSpendPeriodLabel(periodStart, granularity),
      amount: Number(row.total_nis ?? row.amount ?? 0),
      periodStart: String(periodStart || ''),
    };
  });
}

/**
 * Label for institutional Budget dropdown and display.
 * Prefers `number`, then legacy `code`, with name.
 *
 * @param {{ number?: string, code?: string, name?: string }|null|undefined} line
 * @returns {string}
 */
export function formatBudgetLineLabel(line) {
  if (!line) return '';
  const number = String(line.number ?? '').trim();
  const code = String(line.code ?? '').trim();
  const identifier = number || code;
  const name = String(line.name ?? '').trim();
  if (identifier && name) return `${identifier} — ${name}`;
  return identifier || name || '';
}

/**
 * Parse date range from URL search params with calendar-year default.
 *
 * @param {URLSearchParams} searchParams
 * @returns {{ from: string, to: string }}
 */
export function parseLabDataDateRangeFromSearch(searchParams) {
  const defaults = getDefaultDateRange();
  return {
    from: searchParams.get('from') || defaults.from,
    to: searchParams.get('to') || defaults.to,
  };
}
